import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { FaSearch, FaClock, FaCheckCircle, FaExclamationTriangle, FaHourglassHalf, FaTag, FaBrain } from 'react-icons/fa';

// ExpandableSection component
function ExpandableSection({ title, children }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mb-3 sm:mb-4">
      <div
        className="cursor-pointer text-pl_prim_color_1 font-medium flex items-center gap-1 select-none text-sm sm:text-base hover:text-pl_prim_color_3 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <span className="text-xs sm:text-sm">{expanded ? "▼" : "▶"}</span>{" "}
        <span>{title}</span>
      </div>
      {expanded && (
        <div className="p-2 sm:p-3 bg-pl_bg_light_4 rounded mt-1 text-xs sm:text-sm">
          {children}
        </div>
      )}
    </div>
  );
}

const QueryLogDetail = ({ log }) => {
  if (!log) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <FaSearch className="mx-auto text-6xl text-pl_txt_light_3 mb-4" />
          <h3 className="text-lg font-medium text-pl_txt_light_2 mb-2">
            No Query Selected
          </h3>
          <p className="text-sm text-pl_txt_light_3">
            Select a query log from the list to view details
          </p>
        </div>
      </div>
    );
  }

  // Function to render ALL available tags and properties from the log object
  const renderAllLogProperties = () => {
    // Get all properties from the log object
    const allProperties = Object.entries(log);
    const filteredProperties = allProperties.filter(([key]) => 
      !['id', '_id', 'task_id', 'timestamp','referrer'].includes(key)
    );
    
    if (filteredProperties.length === 0) {
      return null;
    }

    return (
      <div className="mb-3 sm:mb-4">
        <div className="space-y-3">
         {filteredProperties.map(([key, value]) => (
            // <ExpandableSection key={key} title={key.replace(/_/g, " ")}>
              <div className="bg-pl_bg_light_5 rounded-lg p-3 sm:p-4 border-l-4 border-l-teal-500">
                <div className="font-medium text-pl_txt_light_1 mb-3 capitalize text-lg">
                  {key.replace(/_/g, " ")}
                </div>

                {value === null ? (
                  <div className="text-pl_txt_light_3 italic">
                    No data available
                  </div>
                ) : value === undefined ? (
                  <div className="text-pl_txt_light_3 italic">Undefined</div>
                ) : typeof value === "object" ? (
                  <div className="space-y-2">
                    {Object.entries(value).map(([subKey, subValue]) => (
                      <div
                        key={subKey}
                        className="bg-pl_bg_light_4 rounded p-3 border-l-2 border-l-teal-300"
                      >
                        <div className="font-medium text-pl_txt_light_1 mb-2 capitalize">
                          {subKey.replace(/_/g, " ")}
                        </div>
                        <div className="text-sm text-pl_txt_light_2">
                          {subValue === null ? (
                            <span className="text-pl_txt_light_3 italic">
                              null
                            </span>
                          ) : subValue === undefined ? (
                            <span className="text-pl_txt_light_3 italic">
                              undefined
                            </span>
                          ) : typeof subValue === "object" ? (
                            <pre className="whitespace-pre-wrap break-words bg-pl_bg_light_3 p-2 rounded text-xs overflow-x-auto">
                              {JSON.stringify(subValue, null, 2)}
                            </pre>
                          ) : Array.isArray(subValue) ? (
                            <div className="space-y-2">
                              {subValue.map((item, index) => (
                                <div
                                  key={index}
                                  className="bg-pl_bg_light_3 rounded p-2 text-xs"
                                >
                                  {typeof item === "object" ? (
                                    <pre className="whitespace-pre-wrap break-words">
                                      {JSON.stringify(item, null, 2)}
                                    </pre>
                                  ) : (
                                    String(item)
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="break-words prose prose-sm max-w-none">
                              <ReactMarkdown>{String(subValue)}</ReactMarkdown>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : Array.isArray(value) ? (
                  <div className="space-y-2">
                    {value.map((item, index) => (
                      <div
                        key={index}
                        className="bg-pl_bg_light_4 rounded p-2 text-xs"
                      >
                        {typeof item === "object" ? (
                          <pre className="whitespace-pre-wrap break-words">
                            {JSON.stringify(item, null, 2)}
                          </pre>
                        ) : (
                          String(item)
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-pl_txt_light_2 break-words whitespace-pre-wrap">
                    {String(value)}
                  </div>
                )}
              </div>
            // </ExpandableSection>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full overflow-y-auto p-3 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 gap-2 sm:gap-0">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-pl_txt_light_1 flex items-center gap-2">
            <FaSearch className="text-pl_prim_color_1 text-base sm:text-lg lg:text-xl" />
            Query Classification Details
          </h2>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-pl_txt_light_3">
          <div className="flex items-center gap-1">
            <FaClock className="text-xs sm:text-sm" />
            <span>{log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}</span>
          </div>
          <span className="hidden sm:inline">•</span>
          <span>ID: {log._id || log.id || 'N/A'}</span>
        </div>
      </div>

      {/* Render ALL available properties and tags automatically */}
      {renderAllLogProperties()}
    </div>
  );
};

export default QueryLogDetail; 